import { useLiveAttention } from "../lib/useLiveAttention";
import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronUp } from "react-feather";
import { useSession } from "../store/session";
import { getQuotations } from "../store/quotations";
import type { Quotation, ReviewReference } from "../lib/reviewReferences";
import { approvalDockState, reviewTitle } from "../lib/approvalReviewState";
import { useReviewSheet } from "../store/reviewSheet";
import ApprovalReview from "./ApprovalReview";
import { getExchanges } from "../store/exchanges";
import { exchangeNeedsOwner, exchangeStatus, visibleExchanges, type Exchange } from "../lib/exchanges";
import "../styles/approval-review.css";

/** One quiet entry point; Auto-approve stays solely in the existing ⋯ menu. */
export default function ApprovalBanner({ onReference, onHandOver }: { onReference: (reference: ReviewReference) => void; onHandOver?: (companyId: string, text: string) => void }) {
  const companyId = useSession(s => s.activeCompanyId);
  const companies = useSession(s => s.companies);
  const attention = useLiveAttention();
  const scope = useReviewSheet(s => s.scope), openReview = useReviewSheet(s => s.open), closeReview = useReviewSheet(s => s.close);
  const open = scope !== null;
  const [quotations, setQuotations] = useState<Quotation[]>([]);
  const [bridgeCompanyIds, setBridgeCompanyIds] = useState<string[]>([]);
  const [error, setError] = useState("");
  const [exchanges, setExchanges] = useState<Exchange[]>([]), [exchangeError, setExchangeError] = useState("");
  const [exchangeCompanyIds, setExchangeCompanyIds] = useState<string[]>([]);
  const dock = useRef<HTMLButtonElement>(null);
  const mounted = useRef(false), sequence = useRef(0);
  const refresh = useCallback(async () => {
    const request = ++sequence.current;
    const [quotes, conversations] = await Promise.allSettled([getQuotations(), getExchanges()]);
    if (!mounted.current || request !== sequence.current) return;
    if (quotes.status === "fulfilled") {
      const next = quotes.value;
      setQuotations(next.quotations); setBridgeCompanyIds(next.bridgeCompanyIds); setError("");
    } else setError(quotes.reason instanceof Error ? quotes.reason.message : "Could not check quotations.");
    if (conversations.status === "fulfilled") { setExchanges(conversations.value.exchanges); setExchangeCompanyIds(conversations.value.capabilityCompanyIds); setExchangeError(""); }
    else setExchangeError(conversations.reason instanceof Error ? conversations.reason.message : "Could not check Exchanges.");
  }, []);
  useEffect(() => {
    mounted.current = true; void refresh();
    const timer = window.setInterval(() => { if (!document.hidden) void refresh(); }, open ? 10_000 : 30_000);
    return () => { mounted.current = false; clearInterval(timer); sequence.current++; };
  }, [refresh, open]);
  if (!companyId) return null;
  const review = approvalDockState(companyId, companies, attention, quotations);
  const currentExchanges = visibleExchanges(exchanges, companies).filter(exchange => exchange.companyId === companyId && exchange.lifecycle !== "ended");
  const exchange = currentExchanges.find(exchangeNeedsOwner) ?? currentExchanges[0];
  const count = review.count + currentExchanges.filter(exchangeNeedsOwner).length;
  const hasWork = review.hasWork || !!exchange, kind = review.hasWork ? review.kind : "Exchange";
  const activity = review.hasWork ? review.activity : exchange ? exchangeStatus(exchange) : null;
  const previewTitle = review.hasWork ? review.title : exchange?.title ?? review.title;
  if (!hasWork && !open) return null;
  const title = review.hasWork ? (error ? "Check connection" : previewTitle) : exchangeError ? "Check connection" : previewTitle;
  const adoptExchange = (next: Exchange) => {
    sequence.current++; // An older in-flight read must not replace this acknowledged mutation.
    setExchanges(current => current.map(item => item.id === next.id && item.companyId === next.companyId ? next : item));
  };
  return <>
    {hasWork && <button ref={dock} type="button" className="reviewDock" onClick={() => openReview("chat")} aria-haspopup="dialog" aria-expanded={open} aria-label="Open approvals and quotations">
      <span className={`reviewPill${kind === "Quotation" ? " is-quotation" : ""}`}>{kind}</span>
      <span className="reviewDock__text"><span className="reviewDock__title">{reviewTitle(title)}</span>
        <span className="reviewDock__summary">{activity && <span className={`reviewActivityDot${activity.active ? " is-active" : ""}`} aria-hidden="true" />}{count ? `${count} for your review` : activity?.label ?? "View Status"}</span></span>
      <span className="reviewDock__arrow"><ChevronUp size={18} aria-hidden="true" /></span>
    </button>}
    {open && <ApprovalReview companyId={companyId} quotations={quotations} error={error}
      exchanges={exchanges} exchangeError={exchangeError} onExchangeUpdate={adoptExchange}
      exchangeCompanyIds={exchangeCompanyIds} onHandOver={onHandOver && ((id, text) => { closeReview(); onHandOver(id, text); })}
      bridgeCompanyIds={bridgeCompanyIds}
      origin={dock.current}
      initialScope={scope ?? "chat"}
      refresh={refresh} onClose={closeReview} onReference={reference => { closeReview(); onReference(reference); }} />}
  </>;
}
